import { useEffect } from 'react'
import {
  Stack,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  styled,
  tableCellClasses,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
} from '@mui/material'
import { CollapsibleSection } from 'components/CollapsibleSection'
import { FileUpload } from 'components/FileUpload'
import {
  useFieldArray,
  useForm,
  Controller,
  FormProvider,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  submissionApi,
  useCreateDocumentSubmissionMutation,
} from 'services/submissions'
import { DocumentRequirement } from 'types/lots'
import { ISubmission } from 'types/submissions'
import {
  AnswerExistance,
  AnswerExistanceOptions,
  DocumentSubmissionRequirement,
  mapToDefaultValues,
  mapDocumentSubmissionToPayload,
} from './map-to-form-values'
import { useDispatch } from 'react-redux'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

type FieldValues = {
  documentRequirements: DocumentSubmissionRequirement[]
}

type Props = {
  documentRequirements: DocumentRequirement[]
  submission: ISubmission
  hasDocDeadlinePassed: boolean
}

export const RequiredDocsSubmission = ({
  documentRequirements,
  submission,
  hasDocDeadlinePassed,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [createDocSubmission, { isSuccess, isError, isUninitialized }] =
    useCreateDocumentSubmissionMutation()

  const defaultFormValues = {
    documentRequirements: mapToDefaultValues({
      documentRequirements,
      documentSubmissions: submission.documentSubmissions ?? [],
    }),
  }

  const methods = useForm({
    defaultValues: defaultFormValues,
  })

  const { control, handleSubmit, watch } = methods

  const items = watch('documentRequirements')
  const isValid = items.every((item) =>
    item.documentRequirementItems
      ?.filter((innerItem) => innerItem.exist === AnswerExistance.EXIST)
      ?.every((innerItem) => innerItem.submittedDocumentFileId)
  )

  const { fields } = useFieldArray({
    control,
    name: `documentRequirements`,
  })

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t])

  const onSubmit = async ({ documentRequirements }: FieldValues) => {
    const body = mapDocumentSubmissionToPayload(documentRequirements, submission)

    try {
      const promises: any =
        body.documentSubmissions &&
        body.documentSubmissions.map((item) => createDocSubmission(item).unwrap())
      const results = await Promise.all(promises)
      dispatch(submissionApi.util.invalidateTags(['MySubmissions']))
    } catch (error) {
      console.error('Error submitting documents:', error)
    }
  }

  return (
    <Stack spacing={1}>
      <FormProvider {...methods}>
        <form>
          {!!fields[0]?.documentRequirementItems?.length && (
            <CollapsibleSection title={t('legal-docs')}>
              <Stack>
                <TableContainer>
                  <Table
                    sx={{
                      tableLayout: 'fixed',
                      '& td, th': {
                        border: '1px solid #ccc',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>
                          {t('evaluationOfDocuments')}
                        </StyledTableCell>
                        <StyledTableCell>
                          {t('contractorOfferAndNotes')}
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields[0]?.documentRequirementItems.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <RequiredDocsSubmissionLeftColumn
                              outerIndex={0}
                              index={index}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <RequiredDocsSubmissionRightColumn
                              outerIndex={0}
                              index={index}
                              hasDocDeadlinePassed={hasDocDeadlinePassed}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </CollapsibleSection>
          )}
          {!!fields[1]?.documentRequirementItems?.length && (
            <CollapsibleSection title={t('speciality')}>
              <Stack>
                <TableContainer>
                  <Table
                    sx={{
                      tableLayout: 'fixed',
                      '& td, th': {
                        border: '1px solid #ccc',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>
                          {t('evaluationOfDocuments')}
                        </StyledTableCell>
                        <StyledTableCell>
                          {t('contractorOfferAndNotes')}
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields[1]?.documentRequirementItems.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <RequiredDocsSubmissionLeftColumn
                              outerIndex={1}
                              index={index}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <RequiredDocsSubmissionRightColumn
                              outerIndex={1}
                              index={index}
                              hasDocDeadlinePassed={hasDocDeadlinePassed}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </CollapsibleSection>
          )}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="contained"
              style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || (!methods.formState.isDirty && isUninitialized)}
            >
              {t('save')}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  )
}

const RequiredDocsSubmissionLeftColumn = ({
  outerIndex,
  index,
}: {
  outerIndex: number
  index: number
}) => {
  const { t } = useTranslation()

  const { control } = useFormContext()

  return (
    <Stack spacing={1.5}>
      <Typography>{`${t('requirement')}: ${index + 1}`}</Typography>
      <Controller
        control={control}
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.criteria`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('requirements')}
            disabled
            variant="outlined"
          />
        )}
      />
      <Controller
        control={control}
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.eligibility`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('evaluationMethod')}
            disabled
            variant="outlined"
          />
        )}
      />
    </Stack>
  )
}

const RequiredDocsSubmissionRightColumn = ({
  outerIndex,
  index,
  hasDocDeadlinePassed,
}: {
  outerIndex: number
  index: number
  hasDocDeadlinePassed: boolean
}) => {
  const { t } = useTranslation()

  const { control, watch } = useFormContext()

  const exist = watch(
    `documentRequirements.${outerIndex}.documentRequirementItems.${index}.exist`
  )

  const isDisabled = exist !== AnswerExistance.EXIST || hasDocDeadlinePassed

  return (
    <Stack spacing={1.5}>
      <Stack spacing={1.5}>
        <Typography>{`${t('answer')}: ${index + 1}`}</Typography>
        <Controller
          control={control}
          name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.exist`}
          render={({ field }) => (
            <Select {...field} size="small">
              {AnswerExistanceOptions.map((existence) => {
                return (
                  <MenuItem key={existence} value={existence}>
                    {t(`existence.${existence}`)}
                  </MenuItem>
                )
              })}
            </Select>
          )}
        />
      </Stack>
      <Controller
        control={control}
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.note`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('notes')}
            required
            disabled={isDisabled}
            variant="outlined"
          />
        )}
      />
      <Controller
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.submittedDocumentFileId`}
        control={control}
        render={({ field }) => (
          <FileUpload
            text={t('document')}
            handleFile={(file) => field.onChange(file.id)}
            fileId={field.value}
            isDisabled={isDisabled}
          />
        )}
      />
    </Stack>
  )
}
